import { SITE_LEVEL_PAGE_ID } from '@wix/a11y-wizard-common';
import { Box, IconButton, Loader } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { RevertSmall } from '@wix/wix-ui-icons-common';
import React, { useMemo } from 'react';
import { AnalysisState, usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useIsEditor3 } from '../../../../../hooks/use-is-editor3';
import { getNumberOfIssuesInVerticalGroup } from '../../../../../rule-mappers/shared/helpers';
import { resolveGroupAnalysisState } from '../../../../../utils/resolve-group-analysis-state';
import { PageList } from '../lists/page-list';
import { SiteLevelTaskList } from '../lists/site-level-task-list';
export const useA11yCardWrapper = _ref => {
  let {
    cardTitle,
    pageIds,
    isCardOpen
  } = _ref;
  const {
    t
  } = useTranslation();
  const isSiteLevelCard = pageIds.includes(SITE_LEVEL_PAGE_ID);
  const {
    panelState
  } = usePanelStateAtom();
  const isEditor3 = useIsEditor3();
  const summary = useMemo(() => {
    let isLoading = false;
    let failedCount = 0;
    let issueCount = 0;
    const total = pageIds.length;
    for (const pageId of pageIds) {
      const group = panelState.groupsDataMap.get(pageId);
      if (!group) {
        // Treat missing group as not failed and with no issues
        continue;
      }
      const analysisState = group.analysisState;
      const {
        isPerformingAnalysis
      } = resolveGroupAnalysisState(analysisState);
      if (analysisState === AnalysisState.FirstAnalyzing || analysisState === AnalysisState.Analyzing) {
        isLoading = true;
      }
      if (analysisState === AnalysisState.AnalyzeFailed) {
        failedCount += 1;
      }
      if (!isPerformingAnalysis) {
        if (group.isTpaPage) {
          issueCount += getNumberOfIssuesInVerticalGroup(panelState, group.itemType) ?? 0;
        } else {
          var _group$analysisData;
          issueCount += ((_group$analysisData = group.analysisData) == null || (_group$analysisData = _group$analysisData.summary) == null ? void 0 : _group$analysisData.issuesCount) ?? 0;
        }
      }
    }
    const isFailed = total > 0 && failedCount === total;
    return {
      isFailed,
      isLoading,
      issueCount
    };
  }, [pageIds, panelState]);
  const {
    isFailed,
    isLoading,
    issueCount
  } = summary;
  const suffix = useMemo(() => {
    if (isLoading) {
      return /*#__PURE__*/React.createElement(Box, {
        verticalAlign: "middle"
      }, /*#__PURE__*/React.createElement(Loader, {
        size: "tiny"
      }));
    }
    if (isFailed) {
      return /*#__PURE__*/React.createElement(IconButton, {
        priority: isEditor3 ? 'tertiary' : 'secondary',
        size: "tiny",
        skin: isEditor3 ? 'dark' : 'standard',
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement(RevertSmall, null));
    }
    return null;
  }, [isLoading, isFailed, isEditor3]);
  const badgeText = useMemo(() => {
    if (isFailed) {
      return t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed');
    }
    if (issueCount > 0) {
      return t('accessibility-wizard.rule-group.issue-count-badge.label', {
        numberOfIssues: issueCount
      });
    }
    return undefined;
  }, [isFailed, issueCount, t]);
  const cardContent = useMemo(() => {
    if (isSiteLevelCard) {
      return /*#__PURE__*/React.createElement(SiteLevelTaskList, {
        isCardOpen: isCardOpen
      });
    }
    return /*#__PURE__*/React.createElement(PageList, {
      pageIds: pageIds
    });
  }, [isSiteLevelCard, isCardOpen, pageIds]);
  return {
    a11yCardProps: {
      badgeText,
      badgeTooltipContent: isFailed ? t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
      disabled: isSiteLevelCard && (isFailed || isLoading),
      id: cardTitle,
      isFailed,
      issueCount: isLoading ? undefined : issueCount,
      suffix,
      title: cardTitle
    },
    cardContent
  };
};
//# sourceMappingURL=use-a11y-card-wrapper.js.map